import BackButton from 'components/Analytics/BackButton'
import AssetHeader from 'components/Analytics/Details/AssetHeader'
import DetailsChartsView from 'components/Analytics/Details/ChartsView'
import AnalyticsDetailsLinks from 'components/Analytics/Details/Links'
import { ChartSkeleton, DetailsLinksSkeleton } from 'components/Analytics/Skeletons'
import PoolsTable from 'components/Analytics/Table/PoolsTable'
import TransactionsTable from 'components/Analytics/Table/TransactionsTable'
import TableManageBar from 'components/Analytics/TokenDetails/TableManageBar'
import TokenStatistics from 'components/Analytics/TokenDetails/TokenStatistics'
import { ANALYTICS_CHART_START_TIMESTAMP } from 'constants/chart'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useAnalyticsTimeWindow } from 'hooks/analytics/useAnalyticsTimeWindow'
import { useTokenPriceData } from 'hooks/analytics/useTokenPriceData'
import { useV2ChartData } from 'hooks/analytics/useV2ChartData'
import { useV2PoolsByToken } from 'hooks/analytics/useV2PoolDatas'
import { useV2TokenDatas } from 'hooks/analytics/useV2TokenDatas'
import { useV2TokenTransactions } from 'hooks/analytics/useV2TokenTransactions'
import { useV3ChartData } from 'hooks/analytics/useV3ChartData'
import { useV3PoolsByToken } from 'hooks/analytics/useV3PoolDatas'
import { useV3TokenDatas } from 'hooks/analytics/useV3TokenDatas'
import { useV3TokenTransactions } from 'hooks/analytics/useV3TokenTransactions'
import { useAllTokens } from 'hooks/Tokens'
import useExplorerLinks from 'hooks/useExplorerLinks'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AnalyticsDetailsLink, AnalyticsVersion } from 'types/analytics'
import { buildAnalyticsPath } from 'utils/analytics'
import { getTokenLogoUri } from 'utils/tokenLogo'

dayjs.extend(utc)

export default function AnalyticsTokenPage() {
  const navigate = useNavigate()
  const { version: versionParam, tokenId: tokenIdParam } = useParams<{
    version?: string
    tokenId?: string
  }>()
  const normalizedVersionParam = versionParam?.toLowerCase()
  const version: AnalyticsVersion = normalizedVersionParam === 'v2' ? 'v2' : 'v3'
  const tokenId = tokenIdParam ?? ''
  const { token: getExplorerLinkByToken } = useExplorerLinks()
  const [tableKind, setTableKind] = useState<'pools' | 'transactions'>('pools')
  const [hideLowTvl, setHideLowTvl] = useState(true)
  const [hideLowVolume, setHideLowVolume] = useState(false)

  useEffect(() => {
    if (!tokenId) return

    const isValidVersion = normalizedVersionParam === 'v2' || normalizedVersionParam === 'v3'

    if (!isValidVersion) {
      navigate(`/explore/v3/tokens/${tokenId}`, { replace: true })
    }
  }, [navigate, normalizedVersionParam, tokenId])

  const isV3Token = version === 'v3'
  const isV2Token = version === 'v2'
  const analyticsTimeWindow = useAnalyticsTimeWindow({
    enabled: Boolean(tokenId),
  })

  const allTokens = useAllTokens()

  const v3TokenData = useV3TokenDatas([tokenId], {
    enabled: isV3Token && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const v2TokenData = useV2TokenDatas([tokenId], {
    enabled: isV2Token && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const { data: v3ChartData, isLoading: isV3ChartLoading } = useV3ChartData(ANALYTICS_CHART_START_TIMESTAMP, 0, {
    tokenAddress: tokenId,
    enabled: isV3Token && Boolean(tokenId),
  })

  const { data: v2ChartData, isLoading: isV2ChartLoading } = useV2ChartData({
    enabled: isV2Token && Boolean(tokenId),
    tokenAddress: tokenId,
  })

  const usingChartData = useMemo(() => (isV3Token ? v3ChartData : v2ChartData), [isV3Token, v2ChartData, v3ChartData])

  const { data: v3TokenTransactions, isLoading: isLoadingV3Transactions } = useV3TokenTransactions(tokenId, {
    enabled: isV3Token && !!tokenId,
  })

  const { data: v2TokenTransactions, isLoading: isLoadingV2Transactions } = useV2TokenTransactions(tokenId, {
    enabled: isV2Token && !!tokenId,
  })

  const { data: v3PoolsByToken, isLoading: isLoadingV3PoolsByToken } = useV3PoolsByToken(tokenId, {
    enabled: isV3Token && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const { data: v2PoolsByToken, isLoading: isLoadingV2PoolsByToken } = useV2PoolsByToken(tokenId, {
    enabled: isV2Token && Boolean(analyticsTimeWindow.timestamps),
    timestamps: analyticsTimeWindow.timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
    blocksRequired: Boolean(analyticsTimeWindow.blocks),
  })

  const poolsByToken = useMemo(
    () => (isV3Token ? v3PoolsByToken : v2PoolsByToken),
    [isV3Token, v3PoolsByToken, v2PoolsByToken]
  )

  const filteredPoolsByToken = useMemo(() => {
    if (!poolsByToken) return poolsByToken

    return poolsByToken.filter((pool) => {
      const tvlCondition = !hideLowTvl || (pool.tvlUSD ?? 0) > 1_000
      const volumeCondition = !hideLowVolume || (pool.volumeUSD ?? 0) > 1_000
      return tvlCondition && volumeCondition
    })
  }, [poolsByToken, hideLowTvl, hideLowVolume])

  const utcCurrentTime = dayjs()
  const startTimestamp = utcCurrentTime.subtract(90, 'days').startOf('hour').unix()

  const isChartLoading = isV3Token ? isV3ChartLoading : isV2ChartLoading
  const isTransactionsLoading = isV3Token ? isLoadingV3Transactions : isLoadingV2Transactions
  const isPoolsByTokenLoading = isV3Token ? isLoadingV3PoolsByToken : isLoadingV2PoolsByToken

  // const isTokenPriceLoading = token0Price.isLoading || token1Price.isLoading

  const usingTransactions = useMemo(() => {
    return isV3Token ? v3TokenTransactions : v2TokenTransactions
  }, [isV3Token, v3TokenTransactions, v2TokenTransactions])

  const currentTokenData = useMemo(
    () => (version === 'v3' ? v3TokenData?.data?.[0] : v2TokenData?.data?.[0]),
    [version, v3TokenData, v2TokenData]
  )

  const { data: tokenPriceData } = useTokenPriceData({ address: tokenId, startTimestamp, version })
  // const { data: tokenPriceData } = useGetTokenPrice(tokenId)

  const adjustedToCurrent = useMemo(() => {
    if (!tokenPriceData || !currentTokenData || tokenPriceData.length === 0) {
      return undefined
    }

    const latestPrice = currentTokenData.priceUSD
    const adjusted = [...tokenPriceData]

    if (!Number.isFinite(latestPrice)) {
      return adjusted
    }

    const latestTimeLabel = dayjs().utc().format('YYYY-MM-DD')
    const lastPoint = adjusted[adjusted.length - 1]

    if (lastPoint?.time === latestTimeLabel) {
      adjusted[adjusted.length - 1] = {
        time: latestTimeLabel,
        value: latestPrice,
      }
    } else {
      adjusted.push({
        time: latestTimeLabel,
        value: latestPrice,
      })
    }

    return adjusted
  }, [tokenPriceData, currentTokenData])

  const showStatisticsSkeleton = !currentTokenData

  const metricsLoaded = currentTokenData?.metrics24hLoaded ?? currentTokenData?.metricsLoaded ?? false

  const fallbackBackButtonURI = useMemo(() => buildAnalyticsPath(version, 'tokens'), [version])

  const tokenLinks = useMemo(() => {
    if (!currentTokenData) return []

    const link: AnalyticsDetailsLink = {
      name: currentTokenData.name,
      address: currentTokenData.address,
      href: getExplorerLinkByToken(currentTokenData.address) ?? '',
      imageURI: getTokenLogoUri(Object.values(allTokens), currentTokenData.address),
    }

    return [link]
  }, [currentTokenData, allTokens, getExplorerLinkByToken])

  const renderTokenStatistics = () => (
    <TokenStatistics
      address={tokenId}
      version={version}
      tvl={{
        value: currentTokenData?.tvlUSD,
        percentageChange: metricsLoaded ? currentTokenData?.tvl24hUSDChange : undefined,
      }}
      fees={{
        value: metricsLoaded ? currentTokenData?.fees24hUSD : undefined,
      }}
      volume24H={{
        value: metricsLoaded ? currentTokenData?.volumeUSD : undefined,
        percentageChange: metricsLoaded ? currentTokenData?.volumeUSDChange : undefined,
      }}
      volume7D={{
        value: metricsLoaded ? currentTokenData?.volumeUSDWeek : undefined,
      }}
    />
  )

  const renderTokenLinks = () =>
    showStatisticsSkeleton ? <DetailsLinksSkeleton rows={1} /> : <AnalyticsDetailsLinks links={tokenLinks} />

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <BackButton fallbackURI={fallbackBackButtonURI} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-15">
        <div className="lg:col-span-2 space-y-6">
          <AssetHeader
            logoURI={getTokenLogoUri(Object.values(allTokens), currentTokenData?.address)}
            name={currentTokenData?.name ?? ''}
            symbol={currentTokenData?.symbol ?? ''}
            version={version}
            links={[getExplorerLinkByToken(tokenId) ?? '']}
            price={{
              value: currentTokenData?.priceUSD,
              change24h: currentTokenData?.priceUSDChange,
            }}
          />

          <div className="w-full flex justify-center items-center">
            {isChartLoading ? (
              <ChartSkeleton className="w-full min-h-[428px]" hasManageBar manageBarItems={3} />
            ) : (
              <DetailsChartsView view="tokens" data={usingChartData} priceData={adjustedToCurrent} />
            )}
          </div>

          <div className="flex flex-col gap-4 md:hidden">
            {renderTokenStatistics()}
            {renderTokenLinks()}
          </div>

          <div className="xl:min-h-100 w-full rounded flex flex-col gap-4" id="table-section">
            <TableManageBar
              view={tableKind}
              onViewChange={setTableKind}
              hideLowTvl={{ value: hideLowTvl, onChange: setHideLowTvl }}
              hideLowVolume={{
                value: hideLowVolume,
                onChange: setHideLowVolume,
              }}
            />
            {tableKind === 'pools' && (
              <PoolsTable
                poolDatas={filteredPoolsByToken}
                maxItems={10}
                isLoading={isPoolsByTokenLoading}
                versionView={version}
              />
            )}
            {tableKind === 'transactions' && (
              <TransactionsTable transactions={usingTransactions} isLoading={isTransactionsLoading} type="tokens" />
            )}
          </div>
        </div>

        <aside className="hidden md:flex lg:col-span-1 flex-col gap-6 pb-20">
          {renderTokenStatistics()}

          {renderTokenLinks()}
        </aside>
      </div>
    </div>
  )
}
